import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';

import {
	DeviceDiscoveryDevice,
	DeviceDiscoveryEvent,
	DeviceDiscoveryTransactions
} from './models/device-discovery.models';

export type IsDeviceDiscoverableFilter = (device: DeviceDiscoveryDevice) => boolean;
export const IS_DISCOVERABLE_VIRTUAL_DEVICE = new InjectionToken<IsDeviceDiscoverableFilter>(
	'IS_DISCOVERABLE_VIRTUAL_DEVICE'
);

export abstract class LiveDeviceDiscoveryApiService {
	public abstract getDiscoveryEvents$(): Observable<DeviceDiscoveryEvent>;
	public abstract getOnlineDeviceCount$(): Observable<number>;
	public abstract getDetectedDeviceCount$(): Observable<number>;
	/**
	 * This emits the deviceId when a device is discovered
	 */
	public abstract getDiscoveryDeviceIdForAnalytics$(): Observable<string>;
	/**
	 * This emits the deviceId when a device is discovered and the state has become online.
	 */
	public abstract getOnlineDeviceIdForAnalytics$(): Observable<string>;
	public abstract getDiscoveredDevices(): DeviceDiscoveryDevice[];

	/**
	 * Creates a transaction stream with add/update/remove device events. The content of each device is
	 * based on the elementQueryFunction, and optional filter specified by deviceIdFilterFunction.
	 *
	 * If throttleMs is specified the discover events are throttled so there always is throttleMs
	 * between the events.
	 *
	 * The elementQueryFunction are responsible for ALL data, incl relevant subscriptions.
	 *
	 * @param elementQueryFunction  (id          => Observable<ElementType>)
	 * @param config : {throttleMs: number, deviceIdFilterFunction (string => boolean) }
	 * @returns Observable({add:ElementType[], update:ElementType[], remove:ElementType[]})
	 */
	public abstract getDiscoveredDevicesTransactionsByQuery$<ElementType>(
		elementQueryFunction: (id: string) => Observable<ElementType>,
		config?: {
			throttleMs?: number;
			deviceIdFilterFunction?: (id: string) => boolean;
		}
	): Observable<DeviceDiscoveryTransactions<ElementType>>;

	/**
	 * Creates device list stream that is updated if a device is added or removed, or if one of the devices is updated.
	 * The content of each device is based on the elementQueryFunction.
	 *
	 * If throttleMs is specified the discover events are throttled so there always is throttleMs
	 * between the events.

	* The elementQueryFunction are responsible for ALL data, incl to setup relevant subscriptions.
	 *
	 * If you have the option, then prefer to use the transaction-based approach.
	 *
	 * @param elementQueryFunction  (id => Observable<ElementType>)
	 * @param config : {throttleMs: number }
	 * @returns Observable(ElementType[])
	 */
	public abstract getDiscoveredDevicesByQuery$<ElementType extends { id: string }>(
		elementQueryFunction: (id: string) => Observable<ElementType>,
		config?: { throttleMs?: number }
	): Observable<ElementType[]>;
}
